import { useNavigate } from "react-router";
import { Modal } from ".";
import { useState, useRef, useEffect } from "react";
import { ExchangeOrder } from "../../Transactions/types/Exchange";
import { cache, request } from "../../../utils";
import Alert from "../alert/Alert";
import { InfoIcon, TimeIcon } from "../../../icons";
import axios from "axios";
import { BLOCKCHAIN_NETWORK, EMAIL_PAYMENT_METHODS, MOMO_COUNTRIES_ISO2, ORDER_STATUS, PAYMENT_METHOD } from "../../../utils/constant";
import CopyButton from "../button/copyButton";
import { getBlockchainName, waitForLoadingToComplete } from "../../../utils/helpers";
import { useOrdersStore } from "../../../stores/useOrderStore";

interface User {
  _id: string;
  token: string;
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  amount: string;
  timeLeft: number;
  order: ExchangeOrder | null;
  buyerDebitPaymentMethod: PAYMENT_METHOD | null;
}

export default function ConfirmationModal({
  isOpen,
  onClose,
  amount,
  order,
  buyerDebitPaymentMethod,
}: ConfirmationModalProps) {
  const [paymentProof, setPaymentProof] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(120); // 2 minutes countdown
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [ bdcPaymentDetails, setBdcPaymentDetails ] = useState<any>(null);
  const [ orderId, setOrderId ] = useState<string>("");
  const { updateOrder } = useOrdersStore();


  // ---- Countdown timer effect
  useEffect(() => {
    if (!isOpen) {
      setCountdown(120); // Reset countdown when modal closes
      return;
    }

    setOrderId(order?.id || "");

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          goToOrders();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!order?.bdcPaymentDetails) return;

    const sellerDetails = Array.isArray(order.bdcPaymentDetails)
      ? order.bdcPaymentDetails
      : [order.bdcPaymentDetails];

    const rateDetails = Array.isArray(order.paymentDetailsFromRate)
      ? order.paymentDetailsFromRate
      : [order.paymentDetailsFromRate];

    const selectedSellerMethod = buyerDebitPaymentMethod || sellerDetails[0]?.paymentMethod;

    const selectedRateDetail = rateDetails.find(
      (detail) => detail?.paymentMethod === selectedSellerMethod
    );

    setBdcPaymentDetails(selectedRateDetail ?? null);

    if (buyerDebitPaymentMethod === PAYMENT_METHOD.STABLE_CURRENCY) {
      setBdcPaymentDetails(order.bdcPaymentDetails);
    }
  }, [order, buyerDebitPaymentMethod]);


  // ---- handle file change
 const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
   if (e.target.files && e.target.files[0]) {
     const file = e.target.files[0];

     const allowedTypes = [
       "image/jpeg",
       "image/png",
       "image/jpg",
       "application/pdf",
       "image/webp",
       "image/heic",
     ];

     if (file.size > 5 * 1024 * 1024) {
       setError("File size must be less than 5MB");
       setTimeout(() => setError(""), 4000);
       return;
     }

     if (!allowedTypes.includes(file.type)) {
       setError("Invalid file type. Only JPG, PNG, JPEG and PDF are allowed.");
       setTimeout(() => setError(""), 4000);
       return;
     }

     setError(""); // Clear any existing error
     setPaymentProof(file);
   }
 };


  // ---- handle confirmation when button clicked
  const handlePaymentConfirmation = async () => {
    if (order?.buyerDebitPaymentMethod === PAYMENT_METHOD.MOBILE_MONEY && !paymentProof) {
      setError("Please upload proof of payment");
      return;
    }

    setIsUploading(true);
    setError(null);
    setSuccess(null);

    try {
      const user = cache.get("USER") as User | null;
      if (!user) {
        throw new Error("Session expired. Please sign in again.");
      }

      const userId = user._id;
      const token = user.token;

      let buyerPaymentProofUrl: string | undefined;

      if (order?.buyerDebitPaymentMethod === PAYMENT_METHOD.MOBILE_MONEY && paymentProof) {
        const filename = paymentProof.name;

        const presignedRes = await axios.get<{
          data: { uploadLink: string; Key: string };
        }>(`${import.meta.env.VITE_USER_GATEWAY_BASE_URL}/file-manager/file-upload-link`, {
          params: {
            filename,
            purpose: "PAYMENT_PROOF",
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (
          !presignedRes?.data?.data?.uploadLink ||
          !presignedRes?.data?.data?.Key
        ) {
          throw new Error("Failed to generate presigned URL");
        }

        const { uploadLink, Key } = presignedRes.data.data;

        const uploadRes = await fetch(uploadLink, {
          method: "PUT",
          body: paymentProof,
          headers: {
            "Content-Type": paymentProof.type,
          },
        });

        if (!uploadRes.ok) {
          throw new Error("Failed to upload payment proof to S3");
        }

        buyerPaymentProofUrl = encodeURI(Key.startsWith("/") ? Key.slice(1) : Key);
      }

      const dataPayload = buyerPaymentProofUrl ? { buyerPaymentProofUrl } : {};

      const idempotencyKey = `BUYERPAID::${userId}-${orderId}-${order?.fromCurrency}-${order?.amount}`;

      const response = await request.post<any>({
        url: `/order/${orderId}/buyer-paid`,
        data: dataPayload,
        headers: {
          Authorization: `Bearer ${token}`,
          idempotencykey: idempotencyKey,
          "Content-Type": "application/json",
        },
      });

      if (response.success) {
        await waitForLoadingToComplete();

        updateOrder(orderId, {
          status: ORDER_STATUS.PROCESSING,
          buyerMadePayment: true,
        });
      }

      if (order?.buyerDebitPaymentMethod === PAYMENT_METHOD.MOBILE_MONEY) {
        setSuccess("Payment confirmation submitted successfully!");
      } else {
        setSuccess("Payment sent");
      };

      setTimeout(() => {
        goToOrders();
      }, 2000);

    } catch (err) {
      console.error("Error confirming payment:", err);
      setError((err as Error).message);
    } finally {
      setIsUploading(false);
    }
  };

  // ---- Navigate to all orders
  const goToOrders = () => {
    navigate("/exchanges");
    onClose();
  };

  // ---- Render bank Code label for USD and GBP
  const renderBankCodeLabel = () => {
    if (!bdcPaymentDetails?.bankCode) return null;

    switch (order?.fromCurrency) {
      case "USD":
        return "Routing No";
      case "GBP":
        return "Sort Code";
      case "EUR":
        return "IBAN";
      case "INR":
        return "IFSC Code";
      case "CAD":
        return "Routing No";
      default:
        return null;
    }
  };

  const renderblockchainName = () => {
    const network = bdcPaymentDetails?.network as unknown as BLOCKCHAIN_NETWORK;
    return getBlockchainName(network);
  };

  // ---- Render payment details
  const renderPaymentDetails = () => {
    if (!bdcPaymentDetails) return null;

    return (
      <div className="mt-6 border-gray-200 pt-6 dark:border-gray-700">
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-300">Amount</span>
            <span className="text-gray-800 dark:text-white">
              {order?.fromCurrencySymbol}
              {amount}
            </span>
          </div>

          {/* BANK_TRANSFER */}
          {(order?.buyerDebitPaymentMethod === PAYMENT_METHOD.BANK_TRANSFER ||
  order?.buyerDebitPaymentMethod === PAYMENT_METHOD.ACH ||
  order?.buyerDebitPaymentMethod === PAYMENT_METHOD.WIRE) && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">
                  Account Name
                </span>
                <span className="text-gray-800 dark:text-white">
                  {bdcPaymentDetails.accountName}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">
                  Account Number
                </span>
                <span className="text-gray-800 dark:text-white">
                  {bdcPaymentDetails.accountNumber}
                </span>
              </div>

              {bdcPaymentDetails.bankCode && renderBankCodeLabel() && (
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    {renderBankCodeLabel()}
                  </span>
                  <span className="text-gray-800 dark:text-white">
                    {bdcPaymentDetails.bankCode}
                  </span>
                </div>
              )}

              {((order.fromCurrency === "NGN" || order.fromCurrency === "CNY" || order.fromCurrency === "ZAR") ||
                MOMO_COUNTRIES_ISO2.has(bdcPaymentDetails.iso2)) &&
                bdcPaymentDetails.providerName && (
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300">
                      Bank Name
                    </span>
                    <span className="text-gray-800 dark:text-white">
                      {bdcPaymentDetails.providerName}
                    </span>
                  </div>
                )}

                {(order?.fromCurrency === "GBP" || order?.fromCurrency === "EUR") &&
  bdcPaymentDetails?.providerName &&
  bdcPaymentDetails.providerName !== bdcPaymentDetails.bankCode && (
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">Bank Name</span>
      <span className="text-gray-800 dark:text-white">
        {bdcPaymentDetails.providerName}
      </span>
    </div>
  )}
  
{(bdcPaymentDetails.holderCity ||
  bdcPaymentDetails.holderStreet ||
  bdcPaymentDetails.holderPostalCode) && (
  <div className="flex justify-between">
    <span className="text-gray-600 dark:text-gray-300">
      Holder's Address
    </span>
    <span className="text-gray-800 dark:text-white text-right">
      {[
        bdcPaymentDetails.holderStreet,
        bdcPaymentDetails.holderCity,
        bdcPaymentDetails.holderPostalCode,
      ]
        .filter(Boolean)
        .join(", ")}
    </span>
  </div>
)}

            </>
          )}

          {/* STABLE_CURRENCY */}
          {order?.buyerDebitPaymentMethod === PAYMENT_METHOD.STABLE_CURRENCY && (
            <>
              <div className="space-y-1">
                {/* Label */}
                <span className="block text-gray-600 dark:text-gray-300">
                  Wallet Address:
                </span>

                {/* Address + Copy */}
                <div className="flex items-start gap-2 max-w-full">
                  <p className="text-gray-800 dark:text-white break-all whitespace-normal flex-1 max-w-full">
                    {bdcPaymentDetails.walletAddress}
                  </p>
                  <CopyButton text={bdcPaymentDetails.walletAddress!} />
                </div>
              </div>

              <div className="flex flex-col mt-3">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    Network:
                  </span>
                  <span className="text-gray-800 dark:text-white">
                    {renderblockchainName()}
                  </span>
                </div>

                <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                  ⚠️ Please use the{" "}
                  <span className="font-semibold">
                    {renderblockchainName()}
                  </span>{" "}
                  network for your payment. Sending via another network will
                  result in a loss of funds.
                </p>
              </div>
            </>
          )}

          {/* MOBILE_MONEY */}
          {order?.buyerDebitPaymentMethod === PAYMENT_METHOD.MOBILE_MONEY && (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">
                  {bdcPaymentDetails.isUssdCode ? "Account Name" : "MoMo Name"}
                </span>
                <span className="text-gray-800 dark:text-white">
                  {bdcPaymentDetails.accountName}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-300">
                  {bdcPaymentDetails.isUssdCode ? "USSD Code" : "MoMo Number"}
                </span>
                <span className="text-gray-800 dark:text-white">
                  {bdcPaymentDetails.isUssdCode
                    ? `${bdcPaymentDetails.accountNumber}${amount}#`
                    : bdcPaymentDetails.accountNumber}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600 dark:text-gray-300">
                  Network Carrier
                </span>
                <div className="flex items-center gap-2">
                  {bdcPaymentDetails.network?.logo && (
                    <img
                      src={bdcPaymentDetails.network.logo}
                      alt="Network Flag"
                      className="h-4 w-4 object-contain"
                    />
                  )}
                  <span className="text-gray-800 dark:text-white">
                    {bdcPaymentDetails.network?.name}
                  </span>
                </div>
              </div>
            </>
          )}

          {(order?.buyerDebitPaymentMethod === PAYMENT_METHOD.WECHAT_PAY ||
  order?.buyerDebitPaymentMethod === PAYMENT_METHOD.ALIPAY) && (
  <>
    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">
        Account Name
      </span>
      <span className="text-gray-800 dark:text-white">
        {bdcPaymentDetails.accountName}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">
        Phone Number
      </span>
      <span className="text-gray-800 dark:text-white">
        {bdcPaymentDetails.phoneNumber}
      </span>
    </div>

    <div className="flex justify-between">
      <span className="text-gray-600 dark:text-gray-300">
        Payment Method
      </span>
      <span className="text-gray-800 dark:text-white">
        {bdcPaymentDetails.paymentMethod}
      </span>
    </div>
  </>
)}


          {/* EMAIL */}
          {order?.buyerDebitPaymentMethod &&
            EMAIL_PAYMENT_METHODS.has(order?.buyerDebitPaymentMethod ?? "") && (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    Account Name
                  </span>
                  <span className="text-gray-800 dark:text-white">
                    {bdcPaymentDetails.accountName}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    Email
                  </span>
                  <span className="text-gray-800 dark:text-white">
                    {bdcPaymentDetails.email}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-300">
                    Payment Method
                  </span>
                  <span className="text-gray-800 dark:text-white">
                  {bdcPaymentDetails.paymentMethod}
                  </span>
                </div>
              </>
            )}
        </div>

        {/* File Upload Section for MoMo */}
        {order?.buyerDebitPaymentMethod === PAYMENT_METHOD.MOBILE_MONEY && (
          <div className="mt-6">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              Please upload proof of payment
            </p>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*,.pdf"
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-2 px-4 border border-gray-300 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 dark:border-gray-600"
            >
              {paymentProof ? paymentProof.name : "Select File"}
            </button>
            {paymentProof && (
              <p className="mt-1 text-sm text-green-600">File selected</p>
            )}
          </div>
        )}
      </div>
    );
  };

  // ---- disable button if payment proof is not uploaded
  const isPaymentButtonDisabled =
    (order?.buyerDebitPaymentMethod === PAYMENT_METHOD.MOBILE_MONEY && !paymentProof) || isUploading;

  const getButtonText = () => {
    if (isUploading) return "Processing...";
    if (order?.status === ORDER_STATUS.SUCCESSFUL) return "Done";
    if (order?.status === ORDER_STATUS.FAILED) return "Try Again";
    return "I've sent the money";
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="max-w-md">
      <div className="p-6">
        {/* Alert Messages */}
        {error && (
          <div className="mb-4">
            <Alert
              variant="error"
              title="Error"
              message={error}
              showLink={false}
            />
          </div>
        )}
        {success && (
          <div className="mb-4">
            <Alert
              variant="success"
              title="Success"
              message={success}
              showLink={false}
            />
          </div>
        )}

        {bdcPaymentDetails?.isUssdCode ? (
          <div className="mb-6 flex items-start gap-3 rounded-lg bg-blue-50 p-4 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
            <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p>
              The USSD code below belongs to Monirates and/or its Partner. Your Payment is safe and secure. Please dial the code to complete your payment
              and upload proof of payment.
            </p>
          </div>
        ) : order?.buyerDebitPaymentMethod === PAYMENT_METHOD.STABLE_CURRENCY ? (
          <div className="mb-6 flex items-start gap-3 rounded-lg bg-blue-50 p-4 text-sm text-blue-700 dark:bg-blue-900/20 dark:text-blue-400">
            <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p>
              The wallet address below belongs to Monirates and/or its Partner. Your Payment is safe and secure. Please transfer the amount to the account.
            </p>
          </div>
        ) :
        (
          <div className="mb-6 flex items-start gap-3 rounded-lg bg-green-50 p-4 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
            <InfoIcon className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <p>
                The account details below belongs to Monirates and/or its Partner. Your Payment is safe and secure. Please transfer the amount to the account.
            </p>
          </div>
        )}

        {renderPaymentDetails()}

        {/* Timer Section */}
        <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <TimeIcon className="h-4 w-4" />
          <span>Page expires in {countdown} secs</span>
        </div>

        <div className="mt-4">
          <button
            onClick={handlePaymentConfirmation}
            disabled={isPaymentButtonDisabled}
            className={`w-full py-3 px-4 bg-[#EB2E67] text-white font-medium rounded-lg transition duration-200 ${
              isPaymentButtonDisabled
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-blue-700 dark:hover:bg-blue-800"
            }`}
          >
            {getButtonText()}
          </button>
        </div>
      </div>
    </Modal>
  );
}
